import type { ItemFieldRow } from './item-field-reader';
import { readInventoryField } from './inventory-raw-readers';
import { phpJsonInteger } from './php-integer-arithmetic';
import { isPhpNumeric, phpJsonFloat } from './php-decimal-values';
import { createInventoryEconomyReader } from './inventory-economy-reader';
import type { InventoryEconomyReader } from './inventory-economy-reader';
import { createStatusLayerReadContext } from './status-row-construction';
import type { StatusOriginLayerDependencies } from './status-row-construction';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const has = (row: unknown,key: string): boolean => arrayLike(row) && Object.prototype.propertyIsEnumerable.call(row,key);
const own = (row: unknown,key: string): unknown => has(row,key) ? (row as Readonly<Record<string,unknown>>)[key] : undefined;
export interface InventoryTotalsReaders {
    computedValue: InventoryEconomyReader;
    layerValue: (row: ItemFieldRow,key: string) => unknown;
    hasLayer: (row: ItemFieldRow,key: string) => boolean;
    missingRootContents?: () => void;
}
function contents(row: ItemFieldRow,readers: InventoryTotalsReaders): unknown {
    if (!has(row,'inventory_in_container')) readers.missingRootContents?.();
    return own(row,'inventory_in_container') ?? null;
}
const privateTwo = (row: ItemFieldRow): boolean => phpJsonInteger(readInventoryField(row,'inventory_private') ?? 0) === 2n;
/** Recursive carrying weight: fixed-weight containers contribute shell mass only. */
export function calculateInventoryWeight(input: unknown,readers: InventoryTotalsReaders): number {
    if (!arrayLike(input)) return 0;
    let total = 0;
    for (const row of Object.values(input)) {
        if (!arrayLike(row) || privateTwo(row)) continue;
        const weight = readers.computedValue(row,'weight','total');
        if (readers.hasLayer(row,'carrying_capacity_fixed_weight')) {
            if (weight !== null) total += phpJsonFloat(weight);
            continue;
        }
        if (weight !== null) total += phpJsonFloat(weight);
        if (readers.hasLayer(row,'inventory_in_container')) total += calculateInventoryWeight(contents(row,readers),readers);
    }
    return total;
}
/** Full contents mass for capacity inside fixed-weight containers. */
export function sumInventoryDescendantWeight(input: unknown,readers: InventoryTotalsReaders): number {
    if (!arrayLike(input)) return 0;
    let total = 0;
    for (const row of Object.values(input)) {
        if (!arrayLike(row) || privateTwo(row)) continue;
        const weight = readers.computedValue(row,'weight','total');
        if (weight !== null) total += phpJsonFloat(weight);
        if (readers.hasLayer(row,'inventory_in_container')) total += sumInventoryDescendantWeight(contents(row,readers),readers);
    }
    return total;
}
/** Flat carry consumer of already compiled gross/total; no layer or child walk. */
export function calculatePossessorInventoryCarryWeight(input: unknown,readers: InventoryTotalsReaders): number {
    if (!arrayLike(input)) return 0;
    let total = 0;
    for (const row of Object.values(input)) {
        if (!arrayLike(row) || privateTwo(row)) continue;
        total += phpJsonFloat(own(own(own(row,'computed'),'weight'),'gross') ?? readers.computedValue(row,'weight','total') ?? 0);
    }
    return total;
}
export function inventoryLineCostGp(row: ItemFieldRow,readers: InventoryTotalsReaders): number {
    if (!arrayLike(row)) throw new TypeError('Line cost requires a PHP array');
    const total = readers.computedValue(row,'cost','total_gp');
    if (total !== null && isPhpNumeric(total)) return phpJsonFloat(total);
    const rawQuantity = readInventoryField(row,'item_qty');
    const quantity = rawQuantity !== null && isPhpNumeric(rawQuantity) ? phpJsonFloat(rawQuantity) : 1;
    const unit = readers.layerValue(row,'cost_gp') !== null && isPhpNumeric(readers.layerValue(row,'cost_gp'))
        ? phpJsonFloat(readers.layerValue(row,'cost_gp')) : 0;
    return quantity * unit;
}
/** The source GP walker includes private==2 rows; only weight walkers skip them. */
export function sumInventoryDescendantCostGp(input: unknown,readers: InventoryTotalsReaders): number {
    if (!arrayLike(input)) return 0;
    let total = 0;
    for (const row of Object.values(input)) {
        if (!arrayLike(row)) continue;
        total += inventoryLineCostGp(row,readers);
        if (readers.hasLayer(row,'inventory_in_container')) {
            const children = contents(row,readers);
            if (arrayLike(children)) total += sumInventoryDescendantCostGp(children,readers);
        }
    }
    return total;
}
export function createInventoryTotalsCompiler(deps: StatusOriginLayerDependencies): {
    carry: (rows: unknown) => number; descendants: (rows: unknown) => number;
    flatCarry: (rows: unknown) => number; cost: (rows: unknown) => number; lineCost: (row: ItemFieldRow) => number;
} {
    const context = createStatusLayerReadContext(deps);
    const economy = createInventoryEconomyReader({resolveInventoryInstance:deps.resolveInventoryInstance,inventoryContext:context.inventoryContext});
    const readers: InventoryTotalsReaders = {computedValue:economy,layerValue:context.layerValue,hasLayer:context.hasLayer,
        missingRootContents:()=>deps.trace?.(['missing-root','inventory_in_container'])};
    return {carry:rows=>calculateInventoryWeight(rows,readers),descendants:rows=>sumInventoryDescendantWeight(rows,readers),
        flatCarry:rows=>calculatePossessorInventoryCarryWeight(rows,readers),cost:rows=>sumInventoryDescendantCostGp(rows,readers),
        lineCost:row=>inventoryLineCostGp(row,readers)};
}
